import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import "./AdminPayoutDesk.css";

const INITIAL_FORM = {
  beneficiaryType: "employee",
  beneficiaryUser: "",
  beneficiaryName: "",
  beneficiaryMobile: "",
  beneficiaryEmail: "",
  payoutMode: "bank_transfer",
  amount: "",
  purpose: "",
  notes: "",
  sourceModule: "manual",
  sourceRef: "",
  bankDetails: {
    accountName: "",
    accountNumber: "",
    ifsc: "",
    bankName: "",
    branch: "",
    upiId: "",
  },
};

const fmtMoney = (value) => {
  const amount = Number(value || 0);
  return `Rs ${amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const fmtDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN");
};

const statusTone = (status = "") => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "released") return "success";
  if (normalized === "approved") return "primary";
  if (normalized === "pending") return "warning";
  if (normalized === "rejected" || normalized === "failed") return "danger";
  return "secondary";
};

const maskAccount = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= 4) return text;
  return `${"*".repeat(Math.max(text.length - 4, 0))}${text.slice(-4)}`;
};

const beneficiaryOptions = ["employee", "supplier", "contractor", "vendor", "other"];

const AdminPayoutDesk = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState("");
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, approved: 0, released: 0, amount: 0 });
  const [users, setUsers] = useState([]);
  const [providerConfig, setProviderConfig] = useState({ razorpayxReady: false, missingKeys: [] });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const [payoutRes, metaRes] = await Promise.all([
        API.get("/admin/payouts"),
        API.get("/admin/payouts/meta"),
      ]);
      setRows(Array.isArray(payoutRes.data?.rows) ? payoutRes.data.rows : []);
      setSummary(payoutRes.data?.summary || { total: 0, pending: 0, approved: 0, released: 0, amount: 0 });
      setUsers(Array.isArray(metaRes.data?.users) ? metaRes.data.users : []);
      setProviderConfig(metaRes.data?.providerConfig || payoutRes.data?.providerConfig || { razorpayxReady: false, missingKeys: [] });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load payout desk");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredUsers = useMemo(
    () => users.filter((user) => String(user.role || "").toLowerCase() === String(form.beneficiaryType || "").toLowerCase()),
    [users, form.beneficiaryType]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesStatus = !statusFilter || String(row.status || "").toLowerCase() === statusFilter;
      const hay = [
        row.beneficiaryName,
        row.beneficiaryType,
        row.beneficiaryMobile,
        row.purpose,
        row.sourceRef,
        row.providerReference,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesQuery = !query.trim() || hay.includes(query.trim().toLowerCase());
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, statusFilter]);

  const selectedUser = useMemo(
    () => users.find((item) => String(item._id) === String(form.beneficiaryUser)),
    [users, form.beneficiaryUser]
  );

  const openCreate = () => {
    setForm(INITIAL_FORM);
    setShowCreate(true);
  };

  const closeCreate = () => {
    if (submitting) return;
    setShowCreate(false);
    setForm(INITIAL_FORM);
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateBank = (key, value) => {
    setForm((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [key]: value,
      },
    }));
  };

  const onSelectBeneficiary = (userId) => {
    const user = users.find((item) => String(item._id) === String(userId));
    setForm((prev) => ({
      ...prev,
      beneficiaryUser: userId,
      beneficiaryName: user?.fullName || prev.beneficiaryName,
      beneficiaryMobile: user?.mobile || prev.beneficiaryMobile,
      beneficiaryEmail: user?.email || prev.beneficiaryEmail,
      bankDetails: {
        ...prev.bankDetails,
        accountName: user?.bank?.accountName || "",
        accountNumber: user?.bank?.accountNumber || "",
        ifsc: user?.bank?.ifsc || "",
        bankName: user?.bank?.bankName || "",
        branch: user?.bank?.branch || "",
      },
    }));
  };

  const submitCreate = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError("");
      await API.post("/admin/payouts", form);
      closeCreate();
      load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create payout");
    } finally {
      setSubmitting(false);
    }
  };

  const runAction = async (id, action, confirmation, payload = {}) => {
    if (!window.confirm(confirmation)) return;
    try {
      setActionId(id);
      setError("");
      await API.patch(`/admin/payouts/${id}/${action}`, payload);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${action} payout`);
    } finally {
      setActionId("");
    }
  };

  return (
    <div className="apd-page">
      <div className="apd-head">
        <div>
          <h2>Payout Desk</h2>
          <p>Employee, supplier, contractor aur vendor payouts yahin raise, approve aur release karo.</p>
        </div>
        <div className="apd-head-actions">
          <Button variant="outline-primary" onClick={() => navigate("/admin/payments/phonepe")}>
            Receive Payment Link
          </Button>
          <Button variant="outline-secondary" onClick={load} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
          <Button onClick={openCreate}>Create Payout</Button>
        </div>
      </div>

      {!providerConfig.razorpayxReady ? (
        <Alert variant="warning" className="apd-config-alert">
          <strong>RazorpayX setup pending.</strong> Credentials milte hi actual payout wire-up ho jayega.
          {Array.isArray(providerConfig.missingKeys) && providerConfig.missingKeys.length ? (
            <div className="small mt-2">Missing env keys: {providerConfig.missingKeys.join(", ")}</div>
          ) : null}
        </Alert>
      ) : (
        <Alert variant="success" className="apd-config-alert">
          RazorpayX config detected. Release flow provider se wire karne ke liye base ready hai.
        </Alert>
      )}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3 apd-summary-row">
        <Col md={3}>
          <Card className="apd-summary-card">
            <Card.Body>
              <div className="apd-summary-label">Total Payouts</div>
              <div className="apd-summary-value">{summary.total || 0}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="apd-summary-card">
            <Card.Body>
              <div className="apd-summary-label">Pending Approval</div>
              <div className="apd-summary-value">{summary.pending || 0}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="apd-summary-card">
            <Card.Body>
              <div className="apd-summary-label">Approved</div>
              <div className="apd-summary-value">{summary.approved || 0}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="apd-summary-card">
            <Card.Body>
              <div className="apd-summary-label">Total Amount</div>
              <div className="apd-summary-value">{fmtMoney(summary.amount || 0)}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="apd-table-card">
        <Card.Body>
          <div className="apd-toolbar">
            <Form.Control
              placeholder="Search beneficiary, purpose, ref..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="apd-status-filter">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="released">Released</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
            </Form.Select>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive hover className="align-middle">
              <thead>
                <tr>
                  <th>Beneficiary</th>
                  <th>Type</th>
                  <th>Mode</th>
                  <th>Amount</th>
                  <th>Purpose</th>
                  <th>Status</th>
                  <th>Bank / UPI</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">No payouts found</td>
                  </tr>
                ) : (
                  filteredRows.map((row) => {
                    const status = String(row.status || "").toLowerCase();
                    const busy = actionId === row._id;
                    return (
                      <tr key={row._id}>
                        <td>
                          <div className="apd-name">{row.beneficiaryName}</div>
                          <div className="text-muted small">{row.beneficiaryMobile || row.beneficiaryEmail || "—"}</div>
                          <div className="text-muted small">Created {fmtDate(row.createdAt)}</div>
                        </td>
                        <td className="text-capitalize">{row.beneficiaryType}</td>
                        <td className="text-capitalize">{String(row.payoutMode || "").replace("_", " ")}</td>
                        <td>{fmtMoney(row.amount)}</td>
                        <td>
                          <div>{row.purpose || "—"}</div>
                          <div className="text-muted small">{row.sourceRef || row.notes || "—"}</div>
                        </td>
                        <td>
                          <Badge bg={statusTone(status)}>{row.status || "pending"}</Badge>
                        </td>
                        <td className="small">
                          {row.payoutMode === "upi"
                            ? row.bankDetails?.upiId || "—"
                            : `${maskAccount(row.bankDetails?.accountNumber)} / ${row.bankDetails?.ifsc || "—"}`}
                        </td>
                        <td>
                          <div className="apd-actions">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={busy || status !== "pending"}
                              onClick={() => runAction(row._id, "approve", `Approve payout for ${row.beneficiaryName}?`)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-success"
                              disabled={busy || status !== "approved"}
                              onClick={() => runAction(row._id, "release", `Release payout for ${row.beneficiaryName}?`)}
                            >
                              Release
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={busy || !["pending", "approved"].includes(status)}
                              onClick={() => runAction(row._id, "reject", `Reject payout for ${row.beneficiaryName}?`)}
                            >
                              Reject
                            </Button>
                          </div>
                          <div className="text-muted small mt-2">
                            {busy ? "Updating..." : row.providerReference || row.providerStatus || "Awaiting action"}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreate} onHide={closeCreate} size="lg" centered>
        <Form onSubmit={submitCreate}>
          <Modal.Header closeButton={!submitting}>
            <Modal.Title>Create Payout</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label>Beneficiary Type</Form.Label>
                <Form.Select value={form.beneficiaryType} onChange={(e) => updateForm("beneficiaryType", e.target.value)}>
                  {beneficiaryOptions.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={8}>
                <Form.Label>Select Existing User</Form.Label>
                <Form.Select value={form.beneficiaryUser} onChange={(e) => onSelectBeneficiary(e.target.value)}>
                  <option value="">Manual entry</option>
                  {filteredUsers.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.fullName} ({user.mobile || user.email || user.role})
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6}>
                <Form.Label>Name</Form.Label>
                <Form.Control value={form.beneficiaryName} onChange={(e) => updateForm("beneficiaryName", e.target.value)} required />
              </Col>
              <Col md={3}>
                <Form.Label>Mobile</Form.Label>
                <Form.Control value={form.beneficiaryMobile} onChange={(e) => updateForm("beneficiaryMobile", e.target.value)} />
              </Col>
              <Col md={3}>
                <Form.Label>Email</Form.Label>
                <Form.Control value={form.beneficiaryEmail} onChange={(e) => updateForm("beneficiaryEmail", e.target.value)} />
              </Col>

              <Col md={4}>
                <Form.Label>Payout Mode</Form.Label>
                <Form.Select value={form.payoutMode} onChange={(e) => updateForm("payoutMode", e.target.value)}>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="manual">Manual</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Amount</Form.Label>
                <Form.Control type="number" min="0" step="0.01" value={form.amount} onChange={(e) => updateForm("amount", e.target.value)} required />
              </Col>
              <Col md={4}>
                <Form.Label>Purpose</Form.Label>
                <Form.Control value={form.purpose} onChange={(e) => updateForm("purpose", e.target.value)} required />
              </Col>

              <Col md={4}>
                <Form.Label>Source Module</Form.Label>
                <Form.Control value={form.sourceModule} onChange={(e) => updateForm("sourceModule", e.target.value)} />
              </Col>
              <Col md={4}>
                <Form.Label>Source Ref</Form.Label>
                <Form.Control value={form.sourceRef} onChange={(e) => updateForm("sourceRef", e.target.value)} />
              </Col>
              <Col md={4}>
                <Form.Label>Notes</Form.Label>
                <Form.Control value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} />
              </Col>

              {form.payoutMode === "upi" ? (
                <Col md={12}>
                  <Form.Label>UPI ID</Form.Label>
                  <Form.Control value={form.bankDetails.upiId} onChange={(e) => updateBank("upiId", e.target.value)} required />
                </Col>
              ) : form.payoutMode === "manual" ? null : (
                <>
                  <Col md={4}>
                    <Form.Label>Account Name</Form.Label>
                    <Form.Control value={form.bankDetails.accountName} onChange={(e) => updateBank("accountName", e.target.value)} required />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Account Number</Form.Label>
                    <Form.Control value={form.bankDetails.accountNumber} onChange={(e) => updateBank("accountNumber", e.target.value)} required />
                  </Col>
                  <Col md={4}>
                    <Form.Label>IFSC</Form.Label>
                    <Form.Control value={form.bankDetails.ifsc} onChange={(e) => updateBank("ifsc", e.target.value.toUpperCase())} required />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control value={form.bankDetails.bankName} onChange={(e) => updateBank("bankName", e.target.value)} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Branch</Form.Label>
                    <Form.Control value={form.bankDetails.branch} onChange={(e) => updateBank("branch", e.target.value)} />
                  </Col>
                </>
              )}
            </Row>

            {selectedUser ? (
              <Alert variant="light" className="mt-3 mb-0">
                Selected user bank auto-fill ho gaya hai. Agar supplier/contractor ke bank details profile me nahi hain to yahin manual enter kar sakte ho.
              </Alert>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeCreate} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating..." : "Create Payout"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPayoutDesk;
